import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/lib/supabase"

import type { Tables } from "../../database.types"

// Vehicle pages redirect early when off, so keep their data fetching after that early return.
export const KEYLESS_DASHBOARD = "keyless_dashboard"

export type FeatureFlag = Tables<"feature_flags">

export function useFeatureFlags() {
  return useQuery({
    queryKey: ["feature-flags"],
    queryFn: async () => {
      const { data, error } = await supabase.from("feature_flags").select("*")

      if (error) throw error
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

const ROLE_COLUMNS: Record<string, keyof FeatureFlag> = {
  Admin: "admin_enabled",
  Manager: "manager_enabled",
  DSP: "dsp_enabled",
  Driver: "driver_enabled",
}

// Undefined is "not resolved yet", not "denied": AuthContext clears `loading` before the claims
// land on a hard refresh, and treating that as no access bounces a legitimate user.
export function useHasFeatureAccess(featureKey: string): boolean | undefined {
  const { claims, loading: authLoading } = useAuth()
  const { data: features, isPending } = useFeatureFlags()

  if (authLoading || isPending || !features || !claims?.user_role) return undefined

  const roleColumn = ROLE_COLUMNS[claims.user_role]
  if (!roleColumn) return false

  const feature = features.find((f) => f.feature_key === featureKey)

  return Boolean(feature?.enabled && feature[roleColumn])
}
